import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import SearchIcon from "@mui/icons-material/Search"
import { Fade, Menu, MenuItem, Stack, TextField } from "@mui/material"
import Box from "@mui/material/Box"
import MuiButton from "@mui/material/Button"
import _ from "lodash"
import { useCallback, useState } from "react"

import {
  CHECKBOX_BORDER_COLOR,
  DELETE_COLOR,
  DISABLED_COLOR
} from "@constants/colors"

import styles from "./styles"
import { useAppContext } from "@contexts/app_context"
import { RoleTypes } from "__generated__/graphql"

interface CustomToolbarProps {
  searchText?: string | undefined
  onSearch: (search: string) => void
  hideBulkAction?: boolean
  concatProps?: {
    rowLength?: number
    toggleDeleteCustomerDialogOpen?: () => void
    toggleDeleteCoupons?: () => void
  }
}
export default function DataTableToolbar({
  searchText,
  onSearch,
  hideBulkAction = true,
  concatProps
}: CustomToolbarProps) {
  const { rowLength, toggleDeleteCustomerDialogOpen, toggleDeleteCoupons } =
    concatProps || {}
  const { me, translate } = useAppContext()
  const [searchTerm, setSearchTerm] = useState(searchText ? searchText : "")
  const [columnFilterRef] = useState<HTMLButtonElement | null>(null)
  const [anchorElAction, setAnchorElAction] = useState<null | HTMLElement>(null)
  const openAction = Boolean(anchorElAction)
  const actions = [
    {
      value: "DELETE",
      label: translate("delete"),
      color: me?.role === RoleTypes.User ? DISABLED_COLOR : DELETE_COLOR
    }
  ]
  const debouncedOnChange = useCallback(
    _.debounce((value: string) => {
      if (value.length >= 3 || value.length === 0) {
        onSearch(value)
      }
    }, 500),
    []
  )

  function handleToggleAction(event: React.MouseEvent<HTMLElement>) {
    openAction === openAction ? false : true

    setAnchorElAction(event.currentTarget)
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    setSearchTerm(e.target.value)
    debouncedOnChange(e.target.value)
  }

  function handleDelete() {
    toggleDeleteCoupons?.()
    toggleDeleteCustomerDialogOpen?.()
    setAnchorElAction(null)
  }

  return (
    <Stack
      sx={{
        ...styles.toolbarContainer,
        width: hideBulkAction ? "17rem" : "25.3rem"
      }}
    >
      <TextField
        fullWidth
        type="search"
        variant="outlined"
        value={searchTerm}
        onChange={handleChange}
        placeholder={translate("search")}
        InputProps={{
          startAdornment: <SearchIcon sx={{ color: "#9B9B9B" }} />
        }}
        sx={{
          " input": {
            padding: "7px 14px 7px 14px!important"
          },
          "& .MuiOutlinedInput-root": {
            "& fieldset": {
              borderColor: CHECKBOX_BORDER_COLOR,
              borderRadius: "0.5rem"
            }
          }
        }}
        label={undefined}
      />
      <Box
        sx={{
          ...styles.actionBox,
          display: hideBulkAction ? "none" : "flex"
        }}
      >
        <MuiButton
          id="composition-button"
          aria-controls={!!columnFilterRef ? "composition-menu" : undefined}
          aria-expanded={!!columnFilterRef ? "true" : undefined}
          aria-haspopup="true"
          disabled={rowLength === 0}
          fullWidth
          disableRipple
          endIcon={<KeyboardArrowDownIcon />}
          size="large"
          sx={{
            ml: 1,
            "&.MuiButtonBase-root:hover": {
              bgcolor: "transparent"
            },
            color: "#000",
            m: 0,
            p: 0,
            minWidth: 0
          }}
          onClick={handleToggleAction}
        >
          {translate("data_table_bulk_action")}
        </MuiButton>
        <Menu
          id="fade-menu"
          MenuListProps={{
            "aria-labelledby": "fade-button"
          }}
          anchorEl={anchorElAction}
          open={!!openAction}
          onClose={() => {
            setAnchorElAction(null)
          }}
          TransitionComponent={Fade}
          sx={{ minWidth: 600, marginTop: "0.3rem" }}
        >
          {actions.map((option) => (
            <MenuItem
              onClick={() =>
                option.value === "DELETE" ? handleDelete?.() : null
              }
              sx={{ color: option.color, minWidth: 125 }}
              key={option.value}
              disabled={me?.role === RoleTypes.User}
            >
              {option.label}
            </MenuItem>
          ))}
        </Menu>
      </Box>
    </Stack>
  )
}
